/**
 * Correcting a value after the document is on the guest's tablet.
 *
 * There was no way to do this. Once a session went live the pane became the
 * mirror, which is a picture: no input, no pencil, and the inbox disabled
 * behind it. A surname the scanner got wrong and the receptionist did not
 * catch before pushing could only be fixed by cancelling the session outright
 * — throwing away the guest's signature — and a field the guest cannot reach
 * at all could not be fixed by anybody.
 *
 * The API always allowed it: `PATCH /sessions/{id}/values` accepts a
 * non-device caller with `sessions:manage` and emits `session.field_changed`
 * into the session room, which the tablet already applies. Nothing called it.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import { api } from '@/lib/api'
import type { SigningSession } from '@/lib/types'
import { useToast } from '@/components/ui/toast'

const SETTLE_MS = 500

export function useSessionValues(session: SigningSession, onChanged: () => void) {
  const toast = useToast()
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(0)

  const pending = useRef(new Map<string, string>())
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const sessionId = session.id

  const send = useCallback(
    async (body: { fields?: Record<string, string>; checkboxes?: Record<string, string[]> }) => {
      setSaving((n) => n + 1)
      try {
        await api.patch(`/sessions/${sessionId}/values`, body)
        onChanged()
        setDrafts((current) => {
          const next = { ...current }
          for (const key of Object.keys(body.fields ?? {})) delete next[key]
          return next
        })
      } catch (error) {
        toast.error('That change was not saved', (error as Error).message)
      } finally {
        setSaving((n) => n - 1)
      }
    },
    [sessionId, onChanged, toast],
  )

  const setValue = useCallback(
    (fieldKey: string, value: string) => {
      setDrafts((current) => ({ ...current, [fieldKey]: value }))
      pending.current.set(fieldKey, value)
      clearTimeout(timers.current.get(fieldKey))
      timers.current.set(
        fieldKey,
        setTimeout(() => {
          timers.current.delete(fieldKey)
          const queued = pending.current.get(fieldKey)
          pending.current.delete(fieldKey)
          if (queued !== undefined) void send({ fields: { [fieldKey]: queued } })
        }, SETTLE_MS),
      )
    },
    [send],
  )

  const setCheckbox = useCallback(
    (groupKey: string, options: string[]) => void send({ checkboxes: { [groupKey]: options } }),
    [send],
  )

  const value = useCallback(
    (fieldKey: string, stored: string) => drafts[fieldKey] ?? stored,
    [drafts],
  )

  return useMemo(
    () => ({ value, setValue, setCheckbox, saving: saving > 0 }),
    [value, setValue, setCheckbox, saving],
  )
}
